"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createStreamingSession } from "@/lib/assembly/streaming-session";
import { postAssemblyClientLog } from "@/lib/assembly/log";
import { postJson } from "@/lib/fetcher";

const EMPTY = { status: "idle", error: null, turns: [], lastTurn: null, transcript: "", finalTranscript: "", partialTranscript: "", clientSessionId: 0 };

/** React state contains transcript/status only; PCM goes directly to sendAudio. */
export function useAssemblyStreaming({
  mode = "dictation", language = "en", recordingMode = "regular",
  enableSpeakerLabels = false, scenario = null, persona = null, simulationType = "",
} = {}) {
  const [state, setState] = useState(EMPTY);
  const sessionRef = useRef(null);
  const connectionModeRef = useRef(null);
  const loadedSignatureRef = useRef(null);
  const mountedRef = useRef(true);
  const optionsRef = useRef(null);
  const options = { purpose: mode, language, recordingMode, scenario, persona, simulationType };
  const signature = JSON.stringify(options);
  optionsRef.current = options;

  const requestOptions = useCallback(() => ({
    ...optionsRef.current,
    ...(connectionModeRef.current === "manual" ? { recordingMode: "manual" } : {}),
    includeConfig: true,
  }), []);

  const connect = useCallback((options = {}) => {
    const requestedMode = options?.mode === "manual" ? "manual" : "default";
    const previous = sessionRef.current;
    if (previous && connectionModeRef.current === requestedMode &&
      !["stopped", "lost"].includes(previous.getSnapshot().status)) return previous.start();
    sessionRef.current = null;
    void previous?.stop();
    connectionModeRef.current = requestedMode;
    let session;
    // Speaker labels are a connection-time setting: a later change reaches the next connect, not this session.
    session = createStreamingSession({
      config: { speakerLabels: enableSpeakerLabels },
      getToken: async signal => {
        const body = requestOptions();
        const requestedSignature = JSON.stringify(optionsRef.current);
        const result = await postJson("/api/voice-agent/token", body, { signal });
        if (!result?.token) throw new Error("The voice service did not return a connection token.");
        if (sessionRef.current === session) loadedSignatureRef.current = requestedSignature;
        return result;
      },
      onState: next => { if (mountedRef.current && sessionRef.current === session) setState(next); },
    });
    sessionRef.current = session;
    return session.start();
  }, [enableSpeakerLabels, requestOptions]);

  const disconnect = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (mountedRef.current) setState(EMPTY);
    await session?.stop();
  }, []);

  const sendAudio = useCallback((pcm, metadata) => sessionRef.current?.sendAudio(pcm, metadata) || false, []);
  const forceEndpoint = useCallback(() => sessionRef.current?.forceEndpoint() || false, []);
  const reset = useCallback(() => {
    sessionRef.current?.reset();
    if (!sessionRef.current && mountedRef.current) setState(EMPTY);
  }, []);
  const finalizeSession = useCallback(async stopCapture => {
    const session = sessionRef.current;
    let captureError = null;
    try { await stopCapture?.(); }
    catch (error) {
      captureError = error;
      postAssemblyClientLog({ event: "capture_stop_failed", details: { message: error.message } });
    }
    // Recorder cleanup must not discard words the transcriber already heard.
    const result = await session?.finalize() || { ...EMPTY, status: "finalized", pendingAudioMs: 0 };
    return { ...result, captureError };
  }, []);
  const getSnapshot = useCallback(() => sessionRef.current?.getSnapshot() || EMPTY, []);
  const retryConnection = useCallback(() => {
    sessionRef.current?.reconnect();
    if (!sessionRef.current) void connect().catch(() => {});
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    const recover = () => {
      if (document.visibilityState !== "hidden") sessionRef.current?.reconnect();
    };
    window.addEventListener("online", recover);
    window.addEventListener("focus", recover);
    document.addEventListener("visibilitychange", recover);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("online", recover);
      window.removeEventListener("focus", recover);
      document.removeEventListener("visibilitychange", recover);
      void disconnect();
    };
  }, [disconnect]);

  // Language/scenario/mode edits update the current connection without losing
  // its turn. Vocabulary is still loaded by the authenticated server helper.
  useEffect(() => {
    const session = sessionRef.current;
    if (!session || state.status !== "live" || loadedSignatureRef.current === signature) return;
    const controller = new AbortController();
    postJson("/api/voice-agent/token", { ...requestOptions(), configOnly: true }, { signal: controller.signal })
      .then(({ config }) => {
        if (controller.signal.aborted || sessionRef.current !== session) return;
        session.updateConfiguration({ ...config, speakerLabels: enableSpeakerLabels });
        loadedSignatureRef.current = signature;
      }).catch(error => {
        if (error.name !== "AbortError" && mountedRef.current) setState(previous => ({ ...previous, error }));
      });
    return () => controller.abort();
  }, [signature, state.status, requestOptions, enableSpeakerLabels]);

  return {
    connect, disconnect, sendAudio, forceEndpoint, reset, finalizeSession, getSnapshot, retryConnection,
    status: state.status, connecting: ["connecting", "reconnecting"].includes(state.status),
    connectionReady: state.status === "live", tokenCircuitOpen: state.status === "lost",
    error: state.error?.message || null,
    transcript: state.finalTranscript, fullTranscript: state.transcript, partialTranscript: state.partialTranscript,
    turns: state.turns, currentTurnTranscript: state.lastTurn?.transcript || "",
    // Auto-submit sends the turn's text, so it waits for the formatted copy.
    isEndOfTurn: state.lastTurn?.formatted === true, turnOrder: state.lastTurn?.turn_order ?? 0,
    clientSessionId: state.clientSessionId,
  };
}
